import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend from "@/utils/fetchToBackend";
import {
  companyKeys,
  contractingKeys,
  dashboardKeys,
  statusCountsKeys,
  teamKeys,
} from "@/lib/query-keys";
import { capture } from "@/lib/posthog";
import type {
  ContractingListResponse,
  CompanyContract,
  DashboardData,
  StatusCounts,
} from "@/types/domain";

// Saved-status counts live on StatusCounts (nav badge) and DashboardData.
// Map a status value to its bucket.
type CountKey =
  | "sinEstado"
  | "revisadas"
  | "negociando"
  | "vendidas"
  | "perdidas";
function statusBucket(status: number | null | undefined): CountKey | null {
  switch (status) {
    case 0:
      return "sinEstado";
    case 1:
      return "revisadas";
    case 2:
      return "negociando";
    case 3:
      return "vendidas";
    case 4:
      return "perdidas";
    default:
      return null;
  }
}

export function useUpdateContractingStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      contracting_id: number;
      status: number | null;
      // Solo con PERDIDA: por qué se cayó. Alimenta el histórico de perdidas.
      lost_reason?: string;
    }) =>
      fetchToBackend("/contractinguser/updateStatus", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    // Optimistically patch the status across every cached list, the company
    // contracts cache and the global saved-count (dashboard) so the table, the
    // kanban board, the company detail star and the nav badge react instantly.
    onMutate: async ({ contracting_id, status }) => {
      await qc.cancelQueries({ queryKey: contractingKeys.lists() });
      await qc.cancelQueries({ queryKey: dashboardKeys.all });
      await qc.cancelQueries({ queryKey: statusCountsKeys.all });
      await qc.cancelQueries({ queryKey: companyKeys.contractsAll() });

      const snapshot = qc.getQueriesData<ContractingListResponse>({
        queryKey: contractingKeys.lists(),
      });
      const companySnapshot = qc.getQueriesData<CompanyContract[]>({
        queryKey: companyKeys.contractsAll(),
      });
      const dashSnapshot = qc.getQueriesData<DashboardData>({
        queryKey: dashboardKeys.all,
      });
      const countsSnapshot = qc.getQueriesData<StatusCounts>({
        queryKey: statusCountsKeys.all,
      });

      // Find the previous status from any cache holding this contract, so we
      // can move it between dashboard buckets correctly.
      let oldStatus: number | null | undefined;
      for (const [, value] of snapshot) {
        const found = value?.contractings?.find(
          (c) => c.contracting_id === contracting_id,
        );
        if (found) {
          oldStatus = found.contractinguser?.contractinguser_status ?? null;
          break;
        }
      }
      if (oldStatus === undefined) {
        for (const [, value] of companySnapshot) {
          const found = value?.find(
            (c) => c.contracting_id === contracting_id,
          );
          if (found) {
            oldStatus = found.contractinguser?.contractinguser_status ?? null;
            break;
          }
        }
      }

      // Solo cambia el estado: el responsable se conserva, o la tarjeta se vería
      // "sin responsable" hasta el siguiente refetch.
      const patchTracking = (c: { contractinguser?: unknown }) =>
        status === null
          ? null
          : {
              ...(c.contractinguser as object | null),
              contractinguser_status: status,
            };

      qc.setQueriesData<ContractingListResponse>(
        { queryKey: contractingKeys.lists() },
        (old) =>
          old?.contractings
            ? {
                ...old,
                contractings: old.contractings.map((c) =>
                  c.contracting_id === contracting_id
                    ? {
                        ...c,
                        contractinguser: patchTracking(c) ?? {
                          contractinguser_status: 0,
                        },
                      }
                    : c,
                ),
              }
            : old,
      );

      qc.setQueriesData<CompanyContract[]>(
        { queryKey: companyKeys.contractsAll() },
        (old) =>
          old?.map((c) =>
            c.contracting_id === contracting_id
              ? { ...c, contractinguser: patchTracking(c) }
              : c,
          ),
      );

      // Move the contract between dashboard buckets (counts are global per user).
      const oldBucket = statusBucket(oldStatus);
      const newBucket = statusBucket(status);
      if (oldBucket !== newBucket) {
        const moveBuckets = <T extends Record<CountKey, number>>(old?: T) => {
          if (!old) return old;
          const next = { ...old };
          if (oldBucket) next[oldBucket] = Math.max(0, next[oldBucket] - 1);
          if (newBucket) next[newBucket] = next[newBucket] + 1;
          return next;
        };
        qc.setQueriesData<DashboardData>(
          { queryKey: dashboardKeys.all },
          moveBuckets,
        );
        qc.setQueriesData<StatusCounts>(
          { queryKey: statusCountsKeys.all },
          moveBuckets,
        );
      }

      return {
        snapshot,
        companySnapshot,
        dashSnapshot,
        countsSnapshot,
        oldStatus,
      };
    },
    onSuccess: (_data, { contracting_id, status }, context) => {
      capture(
        status === null ? "contracting_unsaved" : "contracting_status_changed",
        { contracting_id, from: context?.oldStatus ?? null, to: status },
      );
    },
    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, value]) => qc.setQueryData(key, value));
      context?.companySnapshot?.forEach(([key, value]) =>
        qc.setQueryData(key, value),
      );
      context?.dashSnapshot?.forEach(([key, value]) =>
        qc.setQueryData(key, value),
      );
      context?.countsSnapshot?.forEach(([key, value]) =>
        qc.setQueryData(key, value),
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
      qc.invalidateQueries({ queryKey: statusCountsKeys.all });
      qc.invalidateQueries({ queryKey: contractingKeys.details() });
      qc.invalidateQueries({ queryKey: companyKeys.contractsAll() });
      // Ganadas, perdidas y guardadas del mes salen de acá: sin esto la tarjeta de
      // cierres del tablero esperaba al refresco.
      qc.invalidateQueries({ queryKey: teamKeys.monthlyStatsAll() });
    },
  });
}

export function useTogglePriceItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contracting_id: number; priceitem_id: number }) =>
      fetchToBackend("/contractinguser/togglePriceItem", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
      qc.invalidateQueries({ queryKey: statusCountsKeys.all });
      // Prefix match so the open detail refreshes regardless of which
      // searchProfileId its query key carries.
      qc.invalidateQueries({
        queryKey: [...contractingKeys.details(), vars.contracting_id],
      });
    },
  });
}

/**
 * Adds the active profile's matched items to the saved set (and creates the
 * tracking record if it didn't exist). Used by the star when it's empty or
 * showing the "+N missing items" badge.
 */
export function useMergeProfileItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contracting_id: number; searchprofile_id: number }) =>
      fetchToBackend<{ merged: number }>(
        "/contractinguser/mergeProfileItems",
        { method: "POST", body: JSON.stringify(vars) },
      ),
    onMutate: async ({ contracting_id }) => {
      await qc.cancelQueries({ queryKey: contractingKeys.lists() });
      await qc.cancelQueries({ queryKey: dashboardKeys.all });
      await qc.cancelQueries({ queryKey: statusCountsKeys.all });
      const snapshot = qc.getQueriesData<ContractingListResponse>({
        queryKey: contractingKeys.lists(),
      });
      const dashSnapshot = qc.getQueriesData<DashboardData>({
        queryKey: dashboardKeys.all,
      });
      const countsSnapshot = qc.getQueriesData<StatusCounts>({
        queryKey: statusCountsKeys.all,
      });

      // Was it already saved? Only then we skip bumping the saved count.
      let wasSaved = false;
      for (const [, value] of snapshot) {
        const found = value?.contractings?.find(
          (c) => c.contracting_id === contracting_id,
        );
        if (found) {
          wasSaved = found.contractinguser != null;
          break;
        }
      }

      // Mark saved and clear the "missing items" badge instantly.
      qc.setQueriesData<ContractingListResponse>(
        { queryKey: contractingKeys.lists() },
        (old) =>
          old?.contractings
            ? {
                ...old,
                contractings: old.contractings.map((c) =>
                  c.contracting_id === contracting_id
                    ? {
                        ...c,
                        contractinguser: c.contractinguser ?? {
                          contractinguser_status: 0,
                        },
                        untracked_profile_items_count: 0,
                      }
                    : c,
                ),
              }
            : old,
      );
      if (!wasSaved) {
        qc.setQueriesData<DashboardData>(
          { queryKey: dashboardKeys.all },
          (old) => (old ? { ...old, sinEstado: old.sinEstado + 1 } : old),
        );
        qc.setQueriesData<StatusCounts>(
          { queryKey: statusCountsKeys.all },
          (old) => (old ? { ...old, sinEstado: old.sinEstado + 1 } : old),
        );
      }
      return { snapshot, dashSnapshot, countsSnapshot };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshot?.forEach(([key, value]) => qc.setQueryData(key, value));
      ctx?.dashSnapshot?.forEach(([key, value]) => qc.setQueryData(key, value));
      ctx?.countsSnapshot?.forEach(([key, value]) =>
        qc.setQueryData(key, value),
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
      qc.invalidateQueries({ queryKey: statusCountsKeys.all });
    },
  });
}

/**
 * Removes the active profile's matched items from the saved set (the star
 * action when it's full). If no items remain and the licitación isn't in a CRM
 * stage, the backend drops the tracking entirely. State is reconciled by
 * refetching, since the resulting badge count depends on the profile's matches.
 */
export function useRemoveProfileItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { contracting_id: number; searchprofile_id: number }) =>
      fetchToBackend<{ removed: number; deletedTracking: boolean }>(
        "/contractinguser/removeProfileItems",
        { method: "POST", body: JSON.stringify(vars) },
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
      qc.invalidateQueries({ queryKey: dashboardKeys.all });
      qc.invalidateQueries({ queryKey: statusCountsKeys.all });
    },
  });
}
